import { Button } from '@/src/common/components/Button'
import { Loading } from '@/src/common/components/Loading'
import { SquareOverview } from '@/src/common/components/SquareOverview'
import { Routes } from '@/src/common/enums/routes'
import { formatCurrency } from '@/src/common/utils/general'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { Link, router } from 'expo-router'
import { ScrollView, View, Text, StyleSheet } from 'react-native'
import { useLogError } from '@/src/common/hooks/useLogError'
import { openBrowserAsync } from 'expo-web-browser'
import { useDisableSwipingBackToThePreviousPage } from '@/src/common/hooks/disableSwipingBackToThePreviousPage'
import * as CardApplication from '@/src/experiences/card-application'
import { graphql } from '@api/mesa'
import { useGetUserInformation } from '@/src/common/hooks/useGetUserInformation'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { withCardGate } from '@/src/experiences/card-accounts/withCardGate'
import { useSardine } from '@/src/services/Sardine'
import { useEffect } from 'react'
import { MesaCardImage } from '@/src/common/modules/CardSection'
import { CardApplicationWrapper } from './cardApplicationWrapper'
import { useMediaQuery } from 'react-responsive'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Api } from '@/src/store'
import { useSourcePartnerName } from '@/src/common/hooks/useSourcePartnerName'
import { useMortgageModuleDesign } from '@/src/experiences/mortgage/statsig/store/useMortgageModuleDesign'

export const ApplicationApproved = withCardGate(function ApplicationApproved({
  refetchApplicationStatus,
}) {
  const Alert = useAlert(ApplicationApproved.name)
  const isFixedWidthViewport = useMediaQuery({ minWidth: 720 })
  const onError = useLogError()
  const { refreshUserInformation } = useGetUserInformation()
  const { updateOptions } = useSardine()
  const { sourcePartnerName } = useSourcePartnerName()
  const moduleDesign = useMortgageModuleDesign(sourcePartnerName)
  const { card_name } = moduleDesign
  const mesaCardName = card_name || 'The Mesa Homeowners Card'

  const { data, loading, error } = useLoggedInQuery(
    graphql(`
      query GetCreditLineInformation {
        getCreditLineInformation {
          apr
          creditLine
        }
      }
    `),
    {
      onCompleted: () => {
        void refreshUserInformation()
        void refetchApplicationStatus()
      },
      onError(error) {
        onError(error)
        Alert.alert(
          `We weren't able to get your credit card details right now`,
          `But don't worry - your credit card has been issued. Try coming back later`,
          [{ text: 'OK', onPress: () => router.replace(Routes.Main.route), style: 'default' }],
        )
      },
    },
  )
  const creditLine = data?.getCreditLineInformation.creditLine
  const apr = data?.getCreditLineInformation.apr

  const cardHolderAgreementUri =
    CardApplication.Statsig.ParameterStores.useCardPolicyAndProcedures().get(
      'mesa_cardholder_agreement',
      '',
    )

  // once the card management page should be shown (because the card was issued from the application),
  // we should no longer allow the user to swipe to the previous page from the card application route,
  // which would erroneously cause the user to end up in application-specific states even though the
  // application was already approved and the card was issued
  useDisableSwipingBackToThePreviousPage()

  useEffect(() => {
    void updateOptions({ flow: 'application-approved' })
  }, [updateOptions])

  const cardApplicationsQuery = Api.endpoints['GET /card/onboarding/applications'].useQuery(null, {
    refetchOnMountOrArgChange: true,
  })

  return (
    <CardApplicationWrapper>
      {error ? null : loading ? (
        <Loading />
      ) : (
        <>
          <ScrollView showsVerticalScrollIndicator={false}>
            <View
              style={[
                styles.headerTextContainer,
                isFixedWidthViewport && { justifyContent: 'center' },
              ]}
            >
              <Text
                style={[
                  typography['heading/heading2'],
                  isFixedWidthViewport && { textAlign: 'center' },
                ]}
              >
                Congratulations!
              </Text>
            </View>
            <Text style={[styles.headerSubtext, isFixedWidthViewport && { textAlign: 'center' }]}>
              Your application has been approved
            </Text>
            <View style={styles.squareCardImageContainer}>
              <MesaCardImage style={styles.squareCardImage} resizeMode="contain" />
            </View>
            <SquareOverview
              header={<Text style={styles.squareHeader}>{mesaCardName}</Text>}
              items={[
                { name: 'Credit Line', value: creditLine ? formatCurrency(`${creditLine}`) : '' },
                { name: 'APR', value: apr == null ? '' : `${apr}%` },
              ]}
              style={{ marginBottom: 32 }}
            >
              <View style={{ marginTop: 32 }}>
                <Text style={styles.instructionText}>
                  {/* consider hiding this if the card is activated. as of the time of this writing, it's possible for users to see this screen even after they are approved and using the card management screens, either by deeplinking directly here or by deeplinking to the card benefits (aka card application entrypoint) screen, which in turn redirects here */}
                  Estimated arrival is 7-10 business days.
                </Text>
                <Text style={styles.instructionText}>
                  You can access your virtual Mesa card immediately by using the virtual card
                  directly in-app.
                </Text>
                <Text
                  style={[styles.instructionText, styles.instructionTextLink]}
                  onPress={() => void openBrowserAsync(cardHolderAgreementUri)}
                >
                  Terms and Conditions Apply. See Mesa Cardholder Agreement.
                </Text>
              </View>
            </SquareOverview>
          </ScrollView>
          <View style={{ marginBottom: 0 }}>
            <Link
              asChild
              href={Routes.VirtualCard.route}
              replace
              style={{ textDecorationLine: 'none' }}
              disabled={cardApplicationsQuery.isUninitialized || cardApplicationsQuery.isLoading}
            >
              <Button title="Go to Mesa Card Now" />
            </Link>
          </View>
        </>
      )}
    </CardApplicationWrapper>
  )
})

const styles = StyleSheet.create({
  headerTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 32,
  },
  headerSubtext: {
    ...typography['paragraph/md'],
    color: mesaColorNames['Greyscale/600 #505D64'],
    marginBottom: 32,
  },
  instructionText: {
    marginTop: 16,
    color: mesaColorNames['Greyscale/600 #505D64'],
    ...typography.XS,
  },
  instructionTextLink: {
    textDecorationLine: 'underline',
    color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
  },
  squareCardImageContainer: {
    flex: 1,
    alignItems: 'center',
    marginBottom: 20,
  },
  squareCardImage: {
    height: 189,
    width: 299,
    marginVertical: 10,
  },
  squareHeader: {
    ...typography['heading/heading3'],
    color: mesaColorNames['Greyscale/900 #152438'],
    marginBottom: 32,
  },
})
